// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  auth, 
  onAuthStateChanged, 
  db, 
  doc, 
  getDoc, 
  updateDoc 
} from '../firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user ? `User ${user.uid}` : 'No user');
      
      // Check if we're in the registration process
      const isRegistering = localStorage.getItem('isRegistering') === 'true';
      
      if (user) {
        setCurrentUser(user);
        
        try {
          // Get user profile data from Firestore
          const userDoc = await getDoc(doc(db, "users", user.uid));
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserProfile(userData);
            
            // Check if user is verified
            // User is considered verified if either:
            // 1. Firebase Auth email is verified OR
            // 2. Firestore 'verified' field is true (for backwards compatibility)
            const isUserVerified = user.emailVerified || userData.verified;
            setIsVerified(isUserVerified);
            
            // Update Firestore verified field if email is verified but Firestore isn't
            if (user.emailVerified && !userData.verified) {
              await updateDoc(doc(db, "users", user.uid), {
                verified: true
              });
            }
            
            // Update last login time
            await updateDoc(doc(db, "users", user.uid), {
              lastLogin: new Date().toISOString()
            });
            
            // Set authenticated state
            setIsAuthenticated(true);
            
            // Redirect based on verification status
            if (!isUserVerified && !window.location.pathname.includes('/verify-email')) {
              navigate('/verify-email');
            }
          } else {
            console.error('User document does not exist in Firestore');
            // Firestore document doesn't exist for this user
            // This shouldn't happen normally, but just in case
            setIsAuthenticated(false);
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          setIsAuthenticated(false);
        }
      } else {
        // No user is signed in
        setCurrentUser(null);
        setUserProfile(null);
        setIsAuthenticated(false);
        setIsVerified(false);
        
        // If we're not in the registration process and on a protected route, redirect to login
        if (!isRegistering && window.location.pathname !== '/' && 
            window.location.pathname !== '/login' && 
            window.location.pathname !== '/signup' && 
            window.location.pathname !== '/forgot-password') {
          navigate('/login');
        }
      }
      
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [navigate]);

  // Method to check if the current user is an admin
  const isAdmin = () => {
    return userProfile?.role === 'admin';
  };

  const value = {
    currentUser,
    userProfile,
    isAuthenticated,
    isVerified,
    loading,
    isAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;